<script>
  // the <EsriMapView> component:
  // - creates a MapView instance with optional props (basemap, center, zoom)

  import { onMount, onDestroy } from 'svelte';
  import { loadModules } from 'esri-loader';
  import { get } from 'svelte/store';
  import appConfig from '../appConfig_adaptation_measures.js';
  import { am_selected_adaptations } from './stores_adaptation_measures.js';
  import ToggleAmLayers from './ToggleAMLayers.svelte';
  import PersistentDisclaimer from './PersistentDisclaimer.svelte';

  export let am_extentStore;
  export let am_stormStore;
  export let am_slrYearStore;
  export let am_returnPeriodStore;
  export let am_photos360UrlsStore;
  export let am_criticalFacilitiesLayersStore;
  export let am_criticalFacilitiesStatsStore;
  export let am_sideBySideComparisonStore;
  export let am_climateReadyBostonLayersStore;
  export let am_conceptualBWSCProjectsLayersStore;
  export let am_whichAdaptation;
  
  // props with default values in case none are passed in by a parent
  export let center = [-71.09, 42.34]; // lon, lat
  export let zoom = 12;
  export let showMapViewWidgets = false;


  // reference to the DOM node where this MapView instance will be created
  // see "bind:this={viewContainer}" below
  let viewContainer;

  let adaptationMeasuresWidgetContainer;
  let adaptationMeasuresWidgetContainerCSS = 'none';
  
  let criticalFacilitiesWidgetContainer;
  let criticalFacilitiesWidgetValueCSS = 'none';
  
  let persistentDisclaimerContainer;
  let persistentDisclaimerContainerCSS = 'none';
  
  let adaptationTitleContainer;
  let adaptationTitleContainerCSS = 'flex';
  
  let searchWidget;
  let mapView;
  let clickListener;
  let legendWidget;
  let currentMapName;
  let currentSlrYearText;
  
  let currentSelectedAdaptation = 0;
  
  const MIN_PIXEL_SIZE = 820;

  let showPopups = window.innerWidth > MIN_PIXEL_SIZE ? false : true;


  $: {      
      const slrYear = $am_slrYearStore;
      const stormType = get(am_stormStore);
      const returnPeriod = get(am_returnPeriodStore);


      if (!!slrYear && !!slrYear.viewAdaptationOptions && !!mapView) {
        
        mapView.map.when( () => {
          filter360PhotosLayer(mapView.map, false, false);

          const viewAdaptationOptions  = slrYear.viewAdaptationOptions;
          currentSlrYearText = slrYear.slrText;
          if ($am_sideBySideComparisonStore == true) {
            // Turn off ALL layers for all viewAdaptationOpts
            for (let i =0; i < viewAdaptationOptions.length; i++) {
              toggleOffMapLayersByTitle(mapView.map, viewAdaptationOptions[i].layerTitles || []);
            }
            toggleOffMapLayersByTitle(mapView.map, ["Critical Facilities"]);

            currentMapName = undefined;
            // Find the lowest and highest indexed true values in am_selected_adaptations
            let lowest = -1;
            let highest = -1;
            for (let k = 0; k < $am_selected_adaptations.length; k++) {
              if ($am_selected_adaptations[k] == true) {
                if (lowest === -1) {
                  lowest = k;
                } else {
                  highest = k;
                }
              }
            }


            if (am_whichAdaptation == 0) {
              // turn on the lowest
              if (!!viewAdaptationOptions && !!viewAdaptationOptions[lowest]) {
                toggleOnMapLayersByTitle(mapView.map, viewAdaptationOptions[lowest].layerTitles || []);
                currentMapName = viewAdaptationOptions[lowest].buttonName;
                currentSelectedAdaptation = lowest;
                filterAndQueryCriticalFacilitiesLayer(mapView.map, stormType, returnPeriod, slrYear.yearNumber, slrYear.stormSurgeNumber, slrYear.viewAdaptationOptions[currentSelectedAdaptation].mitigationType);
              // TODO -- filter the photos layer for "this" map
              filter360PhotosLayer(mapView.map, slrYear.yearNumber, lowest);

              } 
            } else if (am_whichAdaptation == 1 && (lowest != highest)) {
              // turn on the highest
              if (!!viewAdaptationOptions && !!viewAdaptationOptions[highest]) {
                toggleOnMapLayersByTitle(mapView.map, viewAdaptationOptions[highest].layerTitles || []);
                currentMapName = viewAdaptationOptions[highest].buttonName;
                currentSelectedAdaptation = highest;                
                filterAndQueryCriticalFacilitiesLayer(mapView.map, stormType, returnPeriod, slrYear.yearNumber, slrYear.stormSurgeNumber, slrYear.viewAdaptationOptions[currentSelectedAdaptation].mitigationType);
                // TODO -- filter the photos layer for "this" map
                filter360PhotosLayer(mapView.map, slrYear.yearNumber, highest);
  
              } 
            } else {
              //logic error, can only be 0 or 1 right now.
              console.log("invalid state, unknown am_whichAdaptation: " + am_whichAdaptation);
            }
            
          } else {
            toggleOffMapLayersByTitle(mapView.map, ["Critical Facilities"]);

              for (let i = 0; i < $am_selected_adaptations.length; i++) {
                if ($am_selected_adaptations[i] === true) {
                  currentMapName = viewAdaptationOptions[i].buttonName;
                  toggleOnMapLayersByTitle(mapView.map, viewAdaptationOptions[i].layerTitles || []);
                  currentSelectedAdaptation = i;
                  // TODO -- filter the photos layer for this adaptation
                  filter360PhotosLayer(mapView.map, slrYear.yearNumber, i);
    
                } else {
                  toggleOffMapLayersByTitle(mapView.map, viewAdaptationOptions[i].layerTitles || []);
                }
              }
              filterAndQueryCriticalFacilitiesLayer(mapView.map, stormType, returnPeriod, slrYear.yearNumber, slrYear.stormSurgeNumber, slrYear.viewAdaptationOptions[currentSelectedAdaptation].mitigationType);
            }
          }
        )
      }
    }

  $: {
    if (!!currentMapName) {
        adaptationTitleContainerCSS = 'flex';
      } else {
        adaptationTitleContainerCSS = 'none';
      }
  }

  $: {
      if (!!$am_extentStore) {
        if (!!mapView && !!mapView.extent && !(mapView.extent.equals($am_extentStore)) && get(am_sideBySideComparisonStore)) {
          console.log("newExtent", $am_extentStore, "is equal", mapView.extent.equals($am_extentStore));
          mapView.extent = $am_extentStore;
        }
      }
    };

  onMount(async () => {
    // set Esri JSAPI options
    const options = {
      version: appConfig.esriVersion,
      css: true
    };

    // load Esri JSAPI modules
    const [
      EsriMap,
      WebMap,
      MapView,
      VectorTileLayer,
      watchUtils
    ] = await loadModules([
      'esri/Map',
      'esri/WebMap',
      'esri/views/MapView',
      'esri/layers/VectorTileLayer',
      'esri/core/watchUtils',
    ], options);

    // depeding on if a storm type has been selected in footer panel 1,
    // figure out if when starting up a MapView to use a default map
    // or load a webmap content item from H&S AGOL org
    let map;
    const stormType = get(am_stormStore);
    if (!stormType) {
      
      map = new EsriMap();
      // add the newspaper basemap as a vector tile layer to the map to show something when no storm type has been selected yet
      var tileLayer = new VectorTileLayer({
        url: 'https://jsapi.maps.arcgis.com/sharing/rest/content/items/dfb04de5f3144a80bc3f9f336228d24a/resources/styles/root.json'
      });
      map.add(tileLayer);
    } else {
      map = new WebMap({
        portalItem: {
          id: appConfig.stormTypes[stormType].webmapId
        }
      });

      const returnPeriod = get(am_returnPeriodStore);      
      const slrYear = get(am_slrYearStore);

      filterWebmapLayers(map, stormType, slrYear, returnPeriod);

    }

    // construct a MapView instance
    mapView = new MapView({
      container: viewContainer,
      map: map,
      center,
      zoom,
      constraints: {
        minZoom: 10,
        rotationEnabled: false
      }
    });

    mapView.ui.add(adaptationTitleContainer, 'manual');

    // Check the width here so we can usee the showPopups flag on start.
    checkWindowWidth();

    if (showMapViewWidgets) {
      const [Search, Expand, Legend] = await loadModules([
        'esri/widgets/Search',
        'esri/widgets/Expand',
        'esri/widgets/Legend'
      ]);

      // add a search widget to a node with id="searchWidget" in the Header.svelte
      // the widget needs reference to a mapview instance,
      // so construct it here to avoid the need to pass around a reference to the mapview instance in other svelte components
      searchWidget = new Search({
        view: mapView,
        popupEnabled: false,
        resultGraphicEnabled: false,
        container: 'searchWidget'
      });

      legendWidget = new Legend({
        view: mapView
      });

      mapView.ui.add(new Expand({
        content: legendWidget,
        expanded: !!!showPopups,
        expandTooltip: 'Expand Legend',
        view: mapView
      }), 'bottom-left');

      criticalFacilitiesWidgetValueCSS = 'flex';

      mapView.ui.add(adaptationMeasuresWidgetContainer, 'top-right');
      adaptationMeasuresWidgetContainerCSS = 'block';

      mapView.ui.add({component: persistentDisclaimerContainer, position: 'top-left', index: 1});
      persistentDisclaimerContainerCSS = 'block';
    }

  

    clickListener = mapView.on('click', (event) => {
      mapView
        .hitTest(event)
        .then((response) => {
          const photoUrls = response.results
            .filter((result) => result.graphic.layer.title.toLowerCase() === appConfig.photos360Layer.title.toLowerCase())
            .map((result) => result.graphic.attributes.AreaDesc);

          am_photos360UrlsStore.set(photoUrls);
        });
    });

    // sync mapview extent with a store
    // (needed when there is a side-by-side view with 2 mapviews)
    mapView.when(() => {
      let firstRun = true;
      watchUtils.whenTrue(mapView, 'stationary', () => {
        // do not set the extent the first time the MapView loads
        if (firstRun) {
          firstRun = false;
        } else {
          // set the store with the new extent information
          am_extentStore.set(mapView.extent);
        }
      });

      // every time the extent store is updated, set this MapView instance's extent to match what is in the shared store,
      // but only if it is a different extent value and if the Boolean 'am_sideBySideComparisonStore' is true
      // (otherwise at JSAPI >= 4.15 the view's 'stationary' property will cycle again and force this into an endless loop)
      // am_extentStore.subscribe((newExtent) => {
      //   if (mapView.extent && !mapView.extent.equals(newExtent) && get(am_sideBySideComparisonStore)) {
      //     console.log("newExtent", newExtent, "is equal", mapView.extent.equals(newExtent));

      //     mapView.extent = newExtent;
      //   }
      // });
    });

    // event listener to check the size of the window
    window.addEventListener("resize", checkWindowWidth);

    // end of onMount lifecycle
  });

  function toggleOffMapLayersByTitle(webMap, toggleOffTitles) {

    // Turn off specified titles
    toggleOffTitles.forEach((layerTitle) => {
      // console.log("HIDE: layer to find is: " + layerTitle);

      // webMap.layers.toArray().forEach(l => {
      //   console.log("layer: " + l.title);
      // });

      webMap.layers
            .toArray()
            .filter((l) => layerTitle !== "Logan" && l.title.toLowerCase() === layerTitle.toLowerCase())
            .forEach((l) => {
              // console.log('matched ' + l.title);
              l.visible = false;
            }
            );
    });
  }

  function toggleOnMapLayersByTitle(webMap, toggleOnTitles) {

    // ALWAYS show the Logan layer.
    toggleOnTitles.push("Logan");

    // SHow specified titles
    toggleOnTitles.forEach((layerTitle) => {
      // console.log("SHOW: layer to find is: " + layerTitle);
      const legendLayerInfos = [];

      webMap.layers
            .toArray()
            .filter((l) => l.title.toLowerCase() === layerTitle.toLowerCase())
            .forEach((l) => {
              if (l.title !== "Logan") { // Don't show the Logan legend -- it has nothing in it currently.
                
                l.visible = true;

                // override webmap settings just in case
                l.legendEnabled = true;

                legendLayerInfos.push({
                  title: 'Storm Analysis Results',
                  layer: l
                })
              }
            });
            
            if (legendWidget && legendLayerInfos.length > 0) {
              legendWidget.layerInfos = legendLayerInfos;
            }
      });

}

  ///

  // subscribe to the am_stormStore to react to a new storm type selection
  const unsubscribeam_stormStore = am_stormStore.subscribe(async (newStormType) => {
    console.log("new storm type", newStormType);
    if (!newStormType || !mapView) {
      return;
    }

      // reset critical facilities layers info store to empty array
    // when switching to a new storm type
    const criticalFacilitiesLayersObject = get(am_criticalFacilitiesLayersStore);
    criticalFacilitiesLayersObject.layers = [];
    criticalFacilitiesLayersObject.layerViews = [];
    am_criticalFacilitiesLayersStore.set(criticalFacilitiesLayersObject);

    // Reset the bwsc projects layer
    const bwscLayerObject = get (am_conceptualBWSCProjectsLayersStore);
    bwscLayerObject.layers = [];
    bwscLayerObject.layerViews = [];
    am_conceptualBWSCProjectsLayersStore.set(bwscLayerObject);

    // Reset CRB layer
    const climateReadyObject = get (am_climateReadyBostonLayersStore);
    climateReadyObject.layers = [];
    climateReadyObject.layerViews = [];
    am_climateReadyBostonLayersStore.set(climateReadyObject);

    const [WebMap] = await loadModules(['esri/WebMap']);

    mapView.map = new WebMap({
      portalItem: {
        id: appConfig.stormTypes[newStormType].webmapId
      }
    });

    const returnPeriod = get(returnPeriodStore);
    const slrYear = get(am_slrYearStore);

    filterWebmapLayers(mapView.map, newStormType, slrYear, returnPeriod);
  });

  // subscribe to the am_slrYearStore to react to a new SLR+SS selection
  const unsubscribeam_slrYearStore = am_slrYearStore.subscribe((newSlrYear) => {
    if (!mapView) {
      return;
    }

    const stormType = get(am_stormStore);
    const returnPeriod = get(am_returnPeriodStore);

    filterWebmapLayers(mapView.map, stormType, newSlrYear, returnPeriod);

  });

  function filterWebmapLayers(webMap, stormType, slrYear, returnPeriod) {
    if (!webMap.when) {
      return;
    }
    
    if (!slrYear || !returnPeriod) {
      // hide siimulation-specific layers and 360 photos layer
      // and clear away critical facilities stats
      webMap.when(() => {
        webMap.layers
          .toArray()
          .forEach((l) => {
            console.log("Found layer: ", l.id, l.title);
            l.visible = false;
          });
      });

      filter360PhotosLayer(webMap, false, false);
      setupAdaptationToggleInUpperRightCorner(webMap);
      filterAndQueryCriticalFacilitiesLayer(webMap, false, false);

      return;
    }

    webMap.when(() => {
      const matchingLayerTitles = slrYear.layerTitles;

      // hide all webmap operational layers so we can turn on a very specific subset of layers
      console.log('... layer titles:');
      webMap.layers
        .toArray()
        .forEach((l) => {
          console.log('        ' + l.title + ' ' + l.type);
          l.visible = false;
        });


      // show only those storm simulation layers that are valid for the current year and RP choices
      // and add them manually to the legend widget
      const legendLayerInfos = [];

      !!matchingLayerTitles && matchingLayerTitles.forEach((matchingLayerTitle) => {
        // console.log('mlt ' + matchingLayerTitle);
        webMap.layers
          .toArray()
          .filter((l) => l.title.toLowerCase() === matchingLayerTitle.toLowerCase())
          .forEach((l) => {
            // console.log('matched ' + l.title);
            l.visible = true;

            // override webmap settings just in case
            l.legendEnabled = true;

            legendLayerInfos.push({
              title: 'Storm Analysis Results',
              layer: l
            })
          });
      });

      if (legendWidget) {
        legendWidget.layerInfos = legendLayerInfos;
      }

      // filter and show the 360 Photos layer
      // filter360PhotosLayer(webMap, slrYear.yearNumber, am_whichAdaptation);

       // filter and perform a summary stats query for the Critical Facilities layer
      console.log("current selected", currentSelectedAdaptation);
      filterAndQueryCriticalFacilitiesLayer(webMap, stormType,returnPeriod.returnPeriodNumber, slrYear.yearNumber, slrYear.stormSurgeNumber, slrYear.mitigationType, slrYear.viewAdaptationOptions[currentSelectedAdaptation].mitigationType);

      setupAdaptationToggleInUpperRightCorner(webMap);

    });
  };

  function setupAdaptationToggleInUpperRightCorner(webMap) {
    webMap.when(() => {
      setupAdaptationMeasuresLayerSpecific(webMap,appConfig.conceptualBWSCProjectsLayer.title.toLowerCase(), am_conceptualBWSCProjectsLayersStore);
      setupAdaptationMeasuresLayerSpecific(webMap,appConfig.climateReadyBostonLayer.title.toLowerCase(), am_climateReadyBostonLayersStore);
    });
    
  };


  function setupAdaptationMeasuresLayerSpecific(webMap, title, store) {
    const theLayer = webMap.layers.find((l) => l.title.toLowerCase() === title.toLowerCase());
    if (!!theLayer) {
      theLayer.legendEnabled = false;
      const storeObject = get(store);
      storeObject.layers.push(theLayer);
      mapView.whenLayerView(theLayer).then(layerView => {
        storeObject.layerViews.push(layerView);
      });

      theLayer.visible = storeObject.visible;

      store.set(storeObject);
    } 
  };

  function filter360PhotosLayer(webMap, yearNumber, whichAdaptation) {

    // lookup adapation filter string from config based on whichAda
    const currentMitigation = appConfig.whichAdaptationToPhotoLayerMap[whichAdaptation];

    // reset photo URLs store to empty array
    // to be only further updated on mapview click
    am_photos360UrlsStore.set([]);

    // show 360 Photo Locations
    // and filter based on current choices
    const photos360Layer = webMap.layers
      .find((l) => l.title.toLowerCase() === appConfig.photos360Layer.title.toLowerCase());

    if (!photos360Layer) {
      return;
    }

    // Clear the photos layer if missing data.
    if (!!!currentMitigation || !!!yearNumber) {
      photos360Layer.visible = false;
      return;
    }

    // override webmap settings just in case
    photos360Layer.legendEnabled = false;

    photos360Layer.visible = true;

    photos360Layer.outFields = appConfig.photos360Layer.outFields;

    const definitionExpression = `Mitigation IN ('${currentMitigation}')  AND SLR = ${yearNumber}`.trim();
    
    console.log("!!!", definitionExpression);

    photos360Layer.definitionExpression = definitionExpression;
  }

  function filterAndQueryCriticalFacilitiesLayer(webMap, stormType, returnPeriodNumber, yearNumber, stormSurgeNumber, mitigationType) {
    // set a definition expression for Critical Facilities layer
    // and get summary stats to show in footer panel 5 when not using the side by side comparison view

    const sideBySideComparisonBoolean = get(am_sideBySideComparisonStore);

    // reset all the stats info values to null if there is only 1 mapview
    // (i.e. if side by side mapviews are NOT currently being viewed)
    if (!sideBySideComparisonBoolean) {
      am_criticalFacilitiesStatsStore.update((statsObject) => {
        Object
        .keys(statsObject)
        .forEach((key) => statsObject[key] = null);

        return statsObject;
      });
    }

    const criticalFacilitiesLayer = webMap.layers
      .find((l) => l.title.toLowerCase() === appConfig.criticalFacilitiesLayer.title.toLowerCase());

    if (!criticalFacilitiesLayer) {
      console.log("no critical facilities layer found");
      return;
    }

    console.log('processing critical facilities layer');

    // override webmap settings just in case
    criticalFacilitiesLayer.legendEnabled = false;

    // update critical facilities layers info store
    // which helps with floating widget functionality
    // and keeps all instances of the crititcal infrastructure layer visibility and filterEffects in sync across mapviews
    const criticalFacilitiesLayersObject = get(am_criticalFacilitiesLayersStore);

    if (criticalFacilitiesLayersObject.layers.every((l) => l.uid !== criticalFacilitiesLayer.uid)) {
      criticalFacilitiesLayersObject.layers.push(criticalFacilitiesLayer);

      mapView.whenLayerView(criticalFacilitiesLayer).then(layerView => {
        console.log("pushing layer view", layerView);
        criticalFacilitiesLayersObject.layerViews.push(layerView);
        handleCriticalFacilitiesLayerFiltering();
      });
    }

    criticalFacilitiesLayer.visible = criticalFacilitiesLayersObject.visible;

    am_criticalFacilitiesLayersStore.set(criticalFacilitiesLayersObject);

    // stop early and hide the layer if there is no RP or SLR+SS options chosen
    if (!returnPeriodNumber || !yearNumber) {
      console.log("no year number, stopping");
      criticalFacilitiesLayer.visible = false;
      return;
    }

    const categories = appConfig.criticalFacilitiesLayer.categories;
    console.log("categories", categories);

    let definitionExpression = `
    ReturnPeriod = ${returnPeriodNumber}
      AND SeaLevelRise = ${yearNumber}
      AND StormSurge ${stormSurgeNumber ? '= ' + stormSurgeNumber : 'IS NULL'}
      AND CriticalFeature2 IN ('${categories.join("','")}')
    `.trim();

    if (currentSelectedAdaptation !== -1) {
      definitionExpression = definitionExpression + ` AND Mitigation = '${mitigationType}'`;
    } else {
      definitionExpression = definitionExpression + ` AND Mitigation = 'blah'`;
    }


    // console.log("definitionExpress", definitionExpression);

    criticalFacilitiesLayer.definitionExpression = definitionExpression;

    // stop early before getting fresh query stats
    // if side by side mapviews are currently being viewed
    if (sideBySideComparisonBoolean) {
      //return;
    }

    queryCritialFacilitiesLayer(criticalFacilitiesLayer, returnPeriodNumber, yearNumber);
  }
  
  function queryCritialFacilitiesLayer(criticalFacilitiesLayer, returnPeriodNumber, yearNumber) {
    console.log("queryCritialFacilitiesLayer", criticalFacilitiesLayer);
    // reset all the stats info values to null if there is only 1 mapview
    am_criticalFacilitiesStatsStore.update((statsObject) => {
      Object
        .keys(statsObject)
        .forEach((key) => statsObject[key] = null);

      return statsObject;
    });

    const query = criticalFacilitiesLayer.createQuery();
    query.groupByFieldsForStatistics = ['CriticalFeature2'];
    query.outStatistics = [{
      statisticType: 'count',
      onStatisticField: 'CriticalFeature2',
      outStatisticFieldName: 'count'
    }];

    try {
    criticalFacilitiesLayer
      .queryFeatures(query)
      .then((results) => {
        am_criticalFacilitiesStatsStore.update((statsObject) => {
          // fill in fresh values if there were any returned from the query
          results.features.forEach((f) => {
            statsObject[f.attributes.CriticalFeature2] = f.attributes.count;
          });

          return statsObject;
        });
      })
      .catch(e => {
      console.log("Failed to query critical layer", e);

      });
      
    } catch (e)
    {
      console.log("Failed to query critical layer", e);
    }
  }

  // cleanup mapview click listener and any store subscriptions
  onDestroy(() => {
    if (clickListener && clickListener.remove) {
      clickListener.remove();
      clickListener = null;
    }

    // Recreate the div for the next search widget
    let searchWidgetBox = document.getElementById('searchWidgetBox');
    let newSearchWidget = document.createElement('div');
    newSearchWidget.id = 'searchWidget';
    newSearchWidget.className = 'search-widget-container';
    searchWidgetBox.appendChild(newSearchWidget);

    // Destroy current search widget, otherwise we get multiples
    !!searchWidget && searchWidget.destroy();
    
    unsubscribeam_stormStore();
    unsubscribeam_slrYearStore();

    // remove event listener that checks window size
    window.removeEventListener("resize", checkWindowWidth);
  });


  // critical facilities layerview filtering
  // wired up to the "closeCalciteDropdown" event due to timing issues of when the "active" property reflects the correct state
  function handleCriticalFacilitiesLayerFiltering(dropDownSelectedItems) {
    if (dropDownSelectedItems) {
      // update the store property that tracks which categories are actively filtered
      am_criticalFacilitiesLayersStore.update(storeValue => {
        // reset all to false
        Object.keys(storeValue.visuallyFilteredCategories)
          .forEach(categoryKey => storeValue.visuallyFilteredCategories[categoryKey] = false);

        // set selected items to true
        dropDownSelectedItems.forEach((selectedCategoryItem) => {
          const categoryKey = selectedCategoryItem.innerText;
          storeValue.visuallyFilteredCategories[categoryKey] = true;
        });

        // update the svelte store
        return storeValue;
      });
    }

    // update the featureLayerView's filterEffect
    const vfc = $am_criticalFacilitiesLayersStore.visuallyFilteredCategories;
    const activeCategories = Object.keys(vfc)
      .filter(categoryKey => vfc[categoryKey]);

    $am_criticalFacilitiesLayersStore.layerViews.forEach(layerView => {
      layerView.effect = {
        filter: {
          where: `CriticalFeature2 IN ('${activeCategories.join("','")}')`
        },
        excludedEffect: 'grayscale(100%) opacity(0%)'
      };
    });

  }

  // function that checks the size of the window
  function checkWindowWidth() {
    // returns a boolean
    return showPopups = window.innerWidth <= MIN_PIXEL_SIZE || window.innerHeight <= MIN_PIXEL_SIZE;
  }
  
</script>

<div class="esri-map-view-container" bind:this={viewContainer}></div>


<div class="adaptation-title-container" style="display: {adaptationTitleContainerCSS}" id="adaptation-adaptation-contaienr" bind:this="{adaptationTitleContainer}">
  <div class="adaptation-title-container-text">
    {currentSlrYearText} / {currentMapName}
  </div>
</div>

<!-- toggle am layers popup  -->
<div 
  style="box-shadow: none !important" 
  bind:this={adaptationMeasuresWidgetContainer}
  id="adaptation-measures-widget-container-outer"
>
<!-- if the screen is smaller than MIN_PIXEL_SIZE, minimize the toggle layers component  --> 
  {#if !!showMapViewWidgets && !!showPopups}
  <div
    class="am-layer-icon"
    style="display: {adaptationMeasuresWidgetContainerCSS}; box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.8)"
    id="adaptation-measures-widget-container-inner"
  >
    <div class="info-icon-container">
      <calcite-icon  class="calcite-info-icon" id="am-layers" icon="information"></calcite-icon>
    </div>
  </div>
  <calcite-popover style="width: 204px" heading="Toggle Layers" label="toggle-am-layers" reference-element="am-layers" closable>
      <ToggleAmLayers 
        {handleCriticalFacilitiesLayerFiltering}
        {adaptationMeasuresWidgetContainerCSS}
        {criticalFacilitiesWidgetContainer} 
        {criticalFacilitiesWidgetValueCSS}
        {am_criticalFacilitiesLayersStore}
        {am_climateReadyBostonLayersStore}
        {am_conceptualBWSCProjectsLayersStore}
      />
    </calcite-popover>
    
  {:else} 
    <ToggleAmLayers 
      {handleCriticalFacilitiesLayerFiltering}
      {adaptationMeasuresWidgetContainerCSS}
      {criticalFacilitiesWidgetContainer} 
      {criticalFacilitiesWidgetValueCSS}
      {am_criticalFacilitiesLayersStore}
      {am_climateReadyBostonLayersStore}
      {am_conceptualBWSCProjectsLayersStore}
    />
  {/if}
</div>
<!-- end toggle am layers popup  -->

<!-- disclaimer popup  -->
<div 
  style="display: flex; box-shadow: none !important" 
  bind:this={persistentDisclaimerContainer}
> 
  <!-- if the screen is MIN_PIXEL_SIZE or less, show an info icon with popup  -->
  {#if !!showMapViewWidgets &&!!showPopups}
    <div
      class="persistent-disclaimer-icon"
      style="display: {persistentDisclaimerContainerCSS}"
      id="persistent-disclaimer-widget-container"
    >
      <div 
        class="info-icon-container" 
        style="height: 32px; width: 32px; box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.8)">
        <calcite-icon 
          class="calcite-info-icon" 
          icon="information"
          id="persistent-disclaimer-container" 
          >
        </calcite-icon>
      </div>
    </div>
    
    <calcite-popover 
      class="disclaimer-popup" 
      heading="Disclaimer" 
      label="disclaimer" 
      reference-element="persistent-disclaimer-container" 
      closable
      
    >
      <div 
        style="margin: 0px" 
        class="persistent-disclaimer-container-text"
      >
        {appConfig.persistentDisclaimerText}
      </div>
    </calcite-popover>
      
    
  <!-- for wider screens, show the persistent disclaimer   -->
  {:else} 
    <PersistentDisclaimer
      {persistentDisclaimerContainer}
      {persistentDisclaimerContainerCSS}
    />
  {/if}
  <!-- end disclaimer popup -->

</div>


<style>
  .esri-map-view-container {
    width: 100%;
    height: 100%;
  }

  .persistent-disclaimer-container-text {
    background-color: rgb(255, 255, 255);
    color: #323232;
    padding: 5px;
    width: 350px;
    /* margin-left: 1em; */
    border: black;
    border-width: thin;
    border-style: solid;
  }

  .adaptation-title-container {
    display: flex;
    justify-content: center;
    width: 100%;
    /* modify z-index so this doesn't obscure other widgets */
    z-index: -1;
    bottom: 30px;
  }

  .adaptation-title-container-text {
    background-color: rgba(64, 64, 64, 0.75);
    color: white;
    padding: 5px 20px;
    border-radius: 4px;
    font-size: 16px;
    pointer-events: none;
    user-select: none;
  }
  
  
</style>
